import { json, getMe } from './api'
import { request } from './session'
import { ApiError, type Paginated } from '../types'
import type { BillingSettings, ChargeDetail, Charge, DefaultImpact, Diagnostic, FinancialCommand, FinancialSnapshot, Integration, IntegrationInput, Operation, PaymentEvent, Provider, ProviderCode, ProviderInput, Receivable } from './financialTypes'
import { sanitizeAuditSnapshot } from './auditSafety'
const data = (method: string, value: unknown): RequestInit => ({ method, body: JSON.stringify(value) })
const financialBase = '/v1/financial/'
const providerBase = '/v1/payment-providers/'
export const getProviders = (query = '') => json<Paginated<Provider>>(`${providerBase}${query}`)
export const saveProvider = (id: string | undefined, value: ProviderInput) => json<Provider>(`${providerBase}${id ? `${id}/` : ''}`, data(id ? 'PATCH' : 'POST', value))
const technicalPath = (kind: ProviderCode) => kind === 'asaas' ? 'asaas-integrations' : 'mercado-pago-integrations'
// Project only documented metadata: credentials can never enter the query cache, even on a regressed response.
function integrationMetadata(value: Integration): Integration {
  return { id: value.id, code: value.code, provider_configuration: value.provider_configuration, environment: value.environment, token_configured: value.token_configured === true, webhook_configured: value.webhook_configured === true }
}
export async function getIntegrations(kind: ProviderCode, query = '') {
  const result = await json<Paginated<Integration>>(`${providerBase}${technicalPath(kind)}/${query}`)
  return { count: result.count, next: result.next, previous: result.previous, results: result.results.map(integrationMetadata) }
}
export async function saveIntegration(kind: ProviderCode, id: string | undefined, value: IntegrationInput) {
  return integrationMetadata(await json<Integration>(`${providerBase}${technicalPath(kind)}/${id ? `${id}/` : ''}`, data(id ? 'PATCH' : 'POST', value)))
}
export const getDiagnostic = (id: string) => json<Diagnostic>(`${providerBase}${id}/diagnostic/`)
export const validateProvider = (id: string, reason: string) => json<Diagnostic>(`${providerBase}${id}/validate/`, data('POST', { reason }))
export const getBillingSettings = () => json<BillingSettings>(`${providerBase}billing-settings/`)
export const saveBillingSettings = (value: { grace_days: number; reconciliation_interval: number; reason: string }) => json<BillingSettings>(`${providerBase}billing-settings/`, data('PATCH', value))
export const getDefaultImpact = (id: string) => json<DefaultImpact>(`${providerBase}${id}/default-impact/`)
export const changeDefaultProvider = (value: { provider_id: string; expected_current_provider_id: string | null; reason: string }) => json<BillingSettings>(`${providerBase}billing-settings/change-default/`, data('POST', value))
export const getCharges = (query = '') => json<Paginated<Charge>>(`${financialBase}charges/${query}`)
export const getCharge = (id: string) => json<ChargeDetail>(`${financialBase}charges/${id}/`)
export const commandCharge = (id: string, command: 'refresh' | 'cancel' | 'refund', value: FinancialCommand) => json<Operation>(`${financialBase}charges/${id}/${command}/`, data('POST', value))
export const getOperations = (query = '') => json<Paginated<Operation>>(`${financialBase}operations/${query}`)
export const reconcileOperation = (id: string, value: FinancialCommand) => json<Operation>(`${financialBase}operations/${id}/reconcile/`, data('POST', value))
export const getPaymentEvents = (query = '') => json<Paginated<PaymentEvent>>(`${financialBase}payment-events/${query}`)
export const reprocessEvent = (id: string, value: FinancialCommand) => json<PaymentEvent>(`${financialBase}payment-events/${id}/reprocess/`, data('POST', value))
export const getReceivables = (query = '') => json<Paginated<Receivable>>(`${financialBase}finance/receivables/${query}`)
export const getFinancialSnapshots = (query = '') => json<Paginated<FinancialSnapshot>>(`${financialBase}finance/snapshots/${query}`)
export async function refreshProviderFinances(id: string, value: FinancialCommand) {
  const response = await request(`${financialBase}finance/providers/${id}/refresh/`, data('POST', value))
  const result: unknown = await response.json()
  // 422 is a persisted failed observation, not a transport error; its ID must stay visible.
  if (!response.ok && response.status !== 422) throw new ApiError(response.status, 'Não foi possível consultar o saldo do provedor.')
  return result as FinancialSnapshot
}
export async function requireFinancialPermission(capability: string) {
  const user = await getMe()
  if (user.role !== 'administrator' || !user.capabilities.includes(capability)) throw new ApiError(403, 'Sua permissão mudou. Atualize a sessão antes de continuar.')
}
export function safeText(value: string | null | undefined) { const safe = sanitizeAuditSnapshot(value); return typeof safe === 'string' && safe ? safe : 'Não informado' }
export async function findOperation(id: string, accountId?: string) {
  let query = `?limit=100${accountId ? `&account_id=${encodeURIComponent(accountId)}` : ''}`
  while (true) {
    const page = await getOperations(query); const found = page.results.find(item => item.id === id)
    if (found) return found
    if (!page.next) return null
    query = new URL(page.next, window.location.origin).search
  }
}
export async function findPaymentEvent(id: string) {
  let query = '?limit=100'
  while (true) {
    const page = await getPaymentEvents(query); const found = page.results.find(item => item.id === id)
    if (found) return found
    if (!page.next) return null
    query = new URL(page.next, window.location.origin).search
  }
}
export async function getChargeOperations(accountId: string, orderId: string) {
  let query = `?account_id=${encodeURIComponent(accountId)}&limit=100`
  const operations: Operation[] = []
  while (true) {
    const page = await getOperations(query)
    operations.push(...page.results.filter(item => item.order_id === orderId))
    if (!page.next) return operations
    query = new URL(page.next, window.location.origin).search
  }
}
